import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { TutorLayout } from '@/components/layout/TutorLayout';
import { LoginPage } from '@/features/auth/LoginPage';
import { CoursesPage } from '@/features/courses/CoursesPage';
import { CourseDetailPage } from '@/features/courses/CourseDetailPage';
import { SportsPage } from '@/features/sports/SportsPage';
import { SportsAcademyDetailPage } from '@/features/sports/SportsAcademyDetailPage';
import { EventsPage } from '@/features/events/EventsPage';
import { EventDetailPage } from '@/features/events/EventDetailPage';
import { AdminDashboard } from '@/features/admin/AdminDashboard';
import { AdminCoursesPage } from '@/features/admin/AdminCoursesPage';
import { AdminCourseForm } from '@/features/admin/AdminCourseForm';
import { AdminSportsPage } from '@/features/admin/AdminSportsPage';
import { AdminSportsForm } from '@/features/admin/AdminSportsForm';
import { AdminEventsPage } from '@/features/admin/AdminEventsPage';
import { AdminEventForm } from '@/features/admin/AdminEventForm';
import { AdminUsersPage } from '@/features/admin/AdminUsersPage';
import { AdminTutorsPage } from '@/features/admin/AdminTutorsPage';
import { AdminTutorForm } from '@/features/admin/AdminTutorForm';
import { AdminCategoriesPage } from '@/features/admin/AdminCategoriesPage';
import { ProtectedRoute } from './ProtectedRoute';
import { useAuthStore } from '@/store/authStore';

function App() {
  const initializeSession = useAuthStore((state) => state.initializeSession);

  useEffect(() => initializeSession(), [initializeSession]);

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Navigate to="/courses" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/admin/login" element={<Navigate to="/login" replace />} />
          <Route path="/admin/register" element={<Navigate to="/login" replace />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/courses/:slug" element={<CourseDetailPage />} />
          <Route path="/sports" element={<SportsPage />} />
          <Route path="/sports/:slug" element={<SportsAcademyDetailPage />} />
          <Route path="/workshops" element={<Navigate to="/events" replace />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:slug" element={<EventDetailPage />} />
        </Route>

        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="courses" element={<AdminCoursesPage />} />
          <Route path="courses/new" element={<AdminCourseForm />} />
          <Route path="courses/:id/edit" element={<AdminCourseForm />} />
          <Route path="sports" element={<AdminSportsPage />} />
          <Route path="sports/new" element={<AdminSportsForm />} />
          <Route path="sports/:id/edit" element={<AdminSportsForm />} />
          <Route path="events" element={<AdminEventsPage />} />
          <Route path="events/new" element={<AdminEventForm defaultCategory="event" />} />
          <Route path="events/:id/edit" element={<AdminEventForm />} />
          <Route path="categories" element={<AdminCategoriesPage />} />
          <Route path="workshops" element={<Navigate to="/admin/events" replace />} />
          <Route path="workshops/new" element={<AdminEventForm defaultCategory="workshop" />} />
          <Route path="workshops/:id/edit" element={<AdminEventForm defaultCategory="workshop" />} />
          <Route path="tutors" element={<AdminTutorsPage />} />
          <Route path="tutors/new" element={<AdminTutorForm />} />
          <Route path="tutors/:id/edit" element={<AdminTutorForm />} />
          <Route path="registrations" element={<AdminUsersPage />} />
          <Route path="interested-users" element={<Navigate to="/admin/registrations" replace />} />
        </Route>

        <Route
          path="/tutor"
          element={
            <ProtectedRoute allowedRoles={['tutor']}>
              <TutorLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/tutor/courses" replace />} />
          <Route path="courses" element={<AdminCoursesPage />} />
          <Route path="courses/new" element={<AdminCourseForm />} />
          <Route path="courses/:id/edit" element={<AdminCourseForm />} />
          <Route path="profile" element={<AdminTutorForm />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
